"use client";

import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for stored user data on mount
  useEffect(() => {
    const checkStoredAuth = async () => {
      try {
        const storedUser = localStorage.getItem("chatapp_user");
        const storedUserToken = localStorage.getItem("token");

        if (storedUser && storedUserToken) {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_API_URL}/auth/profile`,
              {
                headers: { Authorization: `Bearer ${storedUserToken}` },
              }
            );
            setUser(response.data.user);
          } catch (error) {
            console.error("Error fetching user profile:", error);
            localStorage.removeItem("chatapp_user");
            localStorage.removeItem("token");
          }
        }
      } catch (error) {
        console.error("Error loading stored user data:", error);
        localStorage.removeItem("chatapp_user");
        localStorage.removeItem("token");
      } finally {
        setIsLoading(false);
      }
    };

    checkStoredAuth();
  }, []);

  const login = async (userData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email: userData.email,
        password: userData.password,
      });
      const data = response.data;
      delete data.user.role;
      setUser(data.user);
      localStorage.setItem("chatapp_user", JSON.stringify(response.data.user));
      localStorage.setItem("token", response.data.token);

      return response.data;
    } catch (error) {
      console.error("Error logging in:", error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    try {
      localStorage.removeItem("chatapp_user");
      localStorage.removeItem("token");
    } catch (error) {
      console.error("Error removing user data:", error);
    }
  };

  const updateUser = (updates) => {
    if (!user) return;

    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    try {
      localStorage.setItem("chatapp_user", JSON.stringify(updatedUser));
    } catch (error) {
      console.error("Error updating user data:", error);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
